import { useMemo } from "react"
import { shallowEqual } from "react-redux"

import { useRNBSelector } from "../store/rootReducer"

const ACCRUAL_REGISTRATION =
  "/office/fiduciary/register_company/accrual_accounting"
const CASH_REGISTRATION = "/office/fiduciary/register_company/cash_accounting"
const CHOOSE_ACCOUNTING_TYPE = "/office/fiduciary/company_accounting_type"

/**
 * Target route for “add company” / post-login company creation flows.
 *
 * Depends on fiduciary load (`status`), pricing, and default accounting regime.
 * Call sites that `navigate(companyCreationRoute)` inside `useEffect` should list
 * the returned string in the effect dependency array so navigation runs again
 * when fiduciary data finishes loading after the first paint.
 */
type FiduciaryLoadStatus = "UNREGISTERED" | "INVALID" | "SUCCESS"
type PricingType = "per_company_month" | "per_document" | null
type CashOrAccrualMode = "cash" | "accrual" | "both"

export function resolveCompanyCreationRoute(
  fiduciaryStatus: FiduciaryLoadStatus,
  pricingType: PricingType | undefined,
  cashOrAccrual: CashOrAccrualMode
): string {
  if (
    fiduciaryStatus === "SUCCESS" &&
    (pricingType == null || pricingType === "per_document")
  ) {
    return ACCRUAL_REGISTRATION
  }
  if (cashOrAccrual === "cash") {
    return CASH_REGISTRATION
  }
  if (cashOrAccrual === "accrual") {
    return ACCRUAL_REGISTRATION
  }
  return CHOOSE_ACCOUNTING_TYPE
}

export function useCompanyCreationRoute(): string {
  const { fiduciaryStatus, pricingType, cashOrAccrual } = useRNBSelector(
    (state) => ({
      fiduciaryStatus: state.fiduciary.status,
      pricingType: state.fiduciary.active_pricing?.pricing_type ?? null,
      cashOrAccrual: state.fiduciary.cash_or_accrual,
    }),
    shallowEqual
  )

  return useMemo(
    () =>
      resolveCompanyCreationRoute(fiduciaryStatus, pricingType, cashOrAccrual),
    [fiduciaryStatus, pricingType, cashOrAccrual]
  )
}
